import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { ReportDefinition, ReportFolder } from "../../api/types";
import { ReportTabs } from "../../components/ReportTabs";
import { ConfirmDialog } from "../../components/ConfirmDialog";

export default function ReportSettings() {
  const { reportId = "" } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState<ReportDefinition | null>(null);
  const [folders, setFolders] = useState<ReportFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    Promise.all([adminApi.reports.get(reportId), adminApi.folders.list()])
      .then(([r, f]) => {
        setReport(r);
        setFolders(f);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load report"))
      .finally(() => setLoading(false));
  }, [reportId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!report) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await adminApi.reports.update(report.id, report);
      setReport(updated);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!report) return;
    try {
      await adminApi.reports.remove(report.id);
      navigate("/reports");
    } catch (err) {
      setConfirmDelete(false);
      setSaveError(err instanceof Error ? err.message : "Failed to delete report");
    }
  }

  if (loading) return <p>Loading...</p>;
  if (error) return <div className="error-text">{error}</div>;
  if (!report) return null;

  return (
    <div>
      <ReportTabs reportId={reportId} title={report.displayName || report.reportKey} />

      <form className="page-panel" onSubmit={handleSubmit}>
        {saveError && <div className="error-text">{saveError}</div>}
        <div className="form-grid">
          <div className="form-row">
            <label>Display name</label>
            <input
              value={report.displayName}
              onChange={(e) => setReport({ ...report, displayName: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <label>Report key</label>
            <input value={report.reportKey} readOnly disabled />
          </div>
          <div className="form-row">
            <label>Category</label>
            <input
              value={report.category ?? ""}
              onChange={(e) => setReport({ ...report, category: e.target.value })}
            />
          </div>
          <div className="form-row">
            <label>Folder</label>
            <select
              value={report.folderId ?? ""}
              onChange={(e) => setReport({ ...report, folderId: e.target.value || null })}
            >
              <option value="">(unassigned)</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>Status</label>
            <select
              value={report.status}
              onChange={(e) => setReport({ ...report, status: e.target.value as any })}
            >
              <option value="DRAFT">Draft</option>
              <option value="PUBLISHED">Published</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
          <div className="form-row">
            <label>Default format</label>
            <select
              value={report.defaultFormat}
              onChange={(e) => setReport({ ...report, defaultFormat: e.target.value as any })}
            >
              <option value="pdf">PDF</option>
              <option value="docx">DOCX</option>
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>
          </div>
          <div className="form-row">
            <label>Page layout</label>
            <select
              value={report.defaultPageLayout}
              onChange={(e) => setReport({ ...report, defaultPageLayout: e.target.value as any })}
            >
              <option value="LANDSCAPE">Landscape</option>
              <option value="PORTRAIT">Portrait</option>
            </select>
          </div>
          <div className="form-row">
            <label>Page width (in)</label>
            <input
              type="number"
              step="0.1"
              value={report.defaultPageWidth ?? ""}
              onChange={(e) =>
                setReport({ ...report, defaultPageWidth: e.target.value ? Number(e.target.value) : null })
              }
            />
          </div>
          <div className="form-row">
            <label>Page height (in)</label>
            <input
              type="number"
              step="0.1"
              value={report.defaultPageHeight ?? ""}
              onChange={(e) =>
                setReport({ ...report, defaultPageHeight: e.target.value ? Number(e.target.value) : null })
              }
            />
          </div>
          <div className="form-row" style={{ gridColumn: "1 / -1" }}>
            <label>Description</label>
            <input
              value={report.description ?? ""}
              onChange={(e) => setReport({ ...report, description: e.target.value })}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save changes"}
          </button>
          <button type="button" className="btn-danger" onClick={() => setConfirmDelete(true)}>
            Delete report
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete report"
        message={`Delete "${report.displayName}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
